import {
    Box,
    Flex,
    Icon,
    IconButton,
    Table,
    Tbody,
    Text,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { TbTrash } from 'react-icons/tb';
import { SettingsTd, SettingsTh } from '../../../../components/settings/primitives';
import { SETTINGS_BORDER_COLOR } from '../../../../components/settings/primitives/constants';
import VendorSearchField from '../vendors/VendorSearchField';

export interface DrpProhibitedVendorsTableProps {
    value: Array<{ _id: string; title: string }>;
    onChange: (next: Array<{ _id: string; title: string }>) => void;
}

/**
 * Vendors this carrier will not accept parts from: a search to add, and a row
 * per vendor. A table rather than a row of tags, so a long list stays readable
 * and each entry has an obvious way out.
 */
const DrpProhibitedVendorsTable = ({ value, onChange }: DrpProhibitedVendorsTableProps) => (
    <Box>
        <Box mb={3}>
            <VendorSearchField
                placeholder="Search vendors to prohibit..."
                excludeIds={value.map((vendor) => vendor._id)}
                onSelect={(supplier) => {
                    if (value.some((vendor) => vendor._id === supplier._id)) return;
                    onChange([...value, { _id: supplier._id, title: supplier.title }]);
                }}
            />
        </Box>

        <Box
            bg="white"
            borderWidth="1px"
            borderColor={SETTINGS_BORDER_COLOR}
            borderRadius="lg"
            overflow="hidden"
        >
            <Box
                maxH="320px"
                overflow="auto"
                sx={{
                    '& thead': { zIndex: 2 },
                    '& th': {
                        bg: 'gray.50',
                        borderBottomWidth: '1px',
                        borderBottomColor: 'var(--chakra-colors-gray-200)',
                    },
                    '& tbody tr:last-of-type td': { borderBottomWidth: 0 },
                    '& th:first-of-type, & td:first-of-type': { pl: 5 },
                    '& th:last-of-type, & td:last-of-type': { pr: 5 },
                }}
            >
                <Table variant="simple" size="sm" w="100%" sx={{ tableLayout: 'fixed' }}>
                    <Thead position="sticky" top={0}>
                        <Tr bg="gray.50">
                            <SettingsTh>VENDOR</SettingsTh>
                            {/* The trash icon speaks for itself, so the column
                                carries no header. */}
                            <SettingsTh w="90px" />
                        </Tr>
                    </Thead>
                    <Tbody>
                        {value.length === 0 ? (
                            <Tr>
                                <SettingsTd colSpan={2} py={8} textAlign="center">
                                    <Text fontSize="sm" color="gray.500">
                                        No prohibited vendors
                                    </Text>
                                </SettingsTd>
                            </Tr>
                        ) : (
                            value.map((vendor) => (
                                <Tr key={vendor._id} _hover={{ bg: 'gray.25' }}>
                                    <SettingsTd color="gray.700">{vendor.title}</SettingsTd>
                                    <SettingsTd textAlign="center">
                                        <Flex justify="center">
                                            <IconButton
                                                aria-label={`Remove ${vendor.title}`}
                                                icon={
                                                    <Icon
                                                        as={TbTrash as React.ElementType}
                                                        boxSize={4}
                                                    />
                                                }
                                                size="xs"
                                                variant="ghost"
                                                colorScheme="red"
                                                onClick={() =>
                                                    onChange(
                                                        value.filter(
                                                            (candidate) =>
                                                                candidate._id !== vendor._id,
                                                        ),
                                                    )
                                                }
                                            />
                                        </Flex>
                                    </SettingsTd>
                                </Tr>
                            ))
                        )}
                    </Tbody>
                </Table>
            </Box>
        </Box>
    </Box>
);

export default DrpProhibitedVendorsTable;
